import { currentLocale, t } from '../lib/i18n';

/**
 * Ocean Channels in the dashboard (docs/channels/BUILD-SPEC.md §5): types mirrored from the API, honest labels and
 * the SMS segment counter. Words follow §5: «Принято каналом» ≠ «Доставлено (по отчёту оператора)» ≠ «Прочитано» ≠
 * «Цель», never «доставлено» without the provider's event.
 */

export const CHANNELS = ['email', 'sms', 'whatsapp', 'telegram_bot', 'telegram_gateway', 'push', 'web_push'] as const;
export type Channel = (typeof CHANNELS)[number];
export type NonEmailChannel = Exclude<Channel, 'email'>;
export type Signal = 'accepted' | 'delivered' | 'read';

export const CHANNEL_LABEL: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  telegram_bot: 'Telegram-бот',
  telegram_gateway: 'Telegram Gateway',
  push: 'Мобильный push',
  web_push: 'Web Push',
};

/** URL slug of a channel page (/channels/<slug>). */
export const CHANNEL_SLUG: Record<NonEmailChannel, string> = {
  sms: 'sms',
  whatsapp: 'whatsapp',
  telegram_bot: 'telegram',
  telegram_gateway: 'telegram-gateway',
  push: 'push',
  web_push: 'web-push',
};
export const SLUG_CHANNEL: Record<string, NonEmailChannel> = Object.fromEntries(Object.entries(CHANNEL_SLUG).map(([c, s]) => [s, c as NonEmailChannel]));

export const CHANNEL_BLURB: Record<Channel, string> = {
  email: 'Письма через ваш домен — как в разделе «Письма».',
  sms: 'Платформенный SMS MailOcean: подключать ничего не нужно, оплата в счёте.',
  whatsapp: 'Ваш WhatsApp Business (WABA): шаблоны Meta, окно 24 ч. Meta выставляет счёт вам напрямую.',
  telegram_bot: 'Ваш бот: человек сам привязывает чат ссылкой. Бесплатно, но бот не сообщает «доставлено».',
  telegram_gateway: 'Коды подтверждения в Telegram по номеру телефона. Оплачивается вашим аккаунтом Telegram.',
  push: 'Ваше мобильное приложение через FCM и APNs. Доставку видно только по квитанциям приложения.',
  web_push: 'Уведомления в браузере на вашем сайте. Ключи VAPID MailOcean создаёт сам, бесплатно.',
};

/** The strongest honest signal of each channel (BUILD-SPEC §2.2). */
export const CAPABILITY: Record<Channel, Signal> = {
  email: 'delivered',
  sms: 'delivered',
  whatsapp: 'read',
  telegram_bot: 'accepted',
  telegram_gateway: 'read',
  push: 'accepted',
  web_push: 'read',
};
export const SIGNAL_RANK: Record<Signal, number> = { accepted: 1, delivered: 2, read: 3 };
export const SIGNAL_LABEL: Record<Signal, string> = { accepted: 'Принято каналом', delivered: 'Доставлено', read: 'Прочитано' };
export const SIGNAL_HINT: Record<Signal, string> = {
  accepted: 'Поставщик принял сообщение. Это ещё не значит, что оно дошло.',
  delivered: 'Доставлено по отчёту оператора или по квитанции нашего SDK.',
  read: 'Открыто или нажато по событию канала (квитанция, статус read).',
};
export const CODE_CHANNELS: Channel[] = ['sms', 'whatsapp', 'telegram_gateway', 'telegram_bot', 'email'];
export const PAID_CHANNELS: Channel[] = ['sms', 'whatsapp', 'telegram_gateway'];

export const PROVIDER_LABEL: Record<string, string> = {
  fake: 'Тестовый поставщик',
  twilio: 'Twilio',
  messente: 'Messente',
  meta_cloud: 'Meta Cloud API',
  telegram_bot: 'Telegram Bot API',
  telegram_gateway: 'Telegram Gateway',
  fcm: 'Firebase (FCM)',
  apns: 'Apple (APNs)',
  vapid: 'Web Push (VAPID)',
};

export const STEP_STATE: Record<string, { label: string; tone: string }> = {
  planned: { label: 'в плане', tone: 'cancelled' },
  skipped: { label: 'пропущен', tone: 'cancelled' },
  waiting: { label: 'ждёт тихих часов', tone: 'warn' },
  queued: { label: 'в очереди', tone: 'queued' },
  handed: { label: 'передан', tone: 'sent' },
  accepted: { label: 'принят каналом', tone: 'sent' },
  delivered: { label: 'доставлен', tone: 'ok' },
  read: { label: 'прочитан', tone: 'ok' },
  failed: { label: 'отказ', tone: 'bad' },
  unknown: { label: 'неизвестный исход', tone: 'warn' },
  cancelled: { label: 'отменён', tone: 'cancelled' },
};

export const SKIP_REASON: Record<string, string> = {
  no_identity: 'у получателя нет адреса в этом канале',
  identity_invalid: 'адрес недоступен: отозван, недействителен или не подходит каналу (например, стационарный номер для SMS)',
  channel_not_configured: 'канал не подключён или аккаунт не проверен',
  channel_paused: 'канал на паузе',
  no_consent: 'нет согласия на этот канал и цель',
  suppressed: 'адрес в подавлениях канала',
  quiet_hours: 'тихие часы получателя',
  country_blocked: 'страна номера не разрешена в настройках',
  frequency_cap: 'частотный лимит контакта',
  over_budget: 'шаг дороже потолка цены уведомления',
  spend_limit: 'исчерпан дневной лимит расходов',
  template_variant_missing: 'в шаблоне нет варианта для этого канала',
  whatsapp_window_closed: 'окно WhatsApp 24 ч закрыто, а шаблона Meta нет',
};

export type NotificationStatus = 'running' | 'succeeded' | 'failed' | 'stopped' | 'cancelled' | 'expired';
export const NOTIFICATION_STATUS: Record<NotificationStatus, { label: string; tone: string; hint: string }> = {
  running: { label: 'Идёт', tone: 'queued', hint: 'Каскад ещё работает: ждём сигнала или дедлайна шага' },
  succeeded: { label: 'Цель достигнута', tone: 'ok', hint: 'Достигнут сигнал успеха политики' },
  failed: { label: 'Не отправлено', tone: 'bad', hint: 'Каждый шаг пропущен или получил точный отказ' },
  stopped: { label: 'Остановлено', tone: 'warn', hint: 'Отправлено, подтверждения нет — дальше не шлём, чтобы не продублировать' },
  cancelled: { label: 'Отменено', tone: 'cancelled', hint: 'Отменено до передачи; переданные шаги не отзываются' },
  expired: { label: 'Срок кода истёк', tone: 'cancelled', hint: 'Код не ввели вовремя' },
};

export const STOP_REASON: Record<string, string> = {
  unknown: 'Неизвестный исход шага: сообщение могло дойти, поэтому дальше не шлём, чтобы не продублировать.',
  deadline_no_signal: 'Дедлайн шага прошёл без сигнала, а политика велит остановиться (on_timeout: stop).',
  exhausted_unconfirmed: 'Шаги закончились: хотя бы один принят каналом, но подтверждения успеха нет.',
};

export const EVENT_LABEL: Record<string, string> = {
  created: 'Уведомление создано',
  planned: 'Шаг запланирован',
  skipped: 'Шаг пропущен',
  waiting: 'Ждёт тихих часов',
  queued: 'Поставлен в очередь',
  held: 'Удержан',
  handed_to_provider: 'Передан поставщику',
  accepted: 'Принято каналом',
  retry_scheduled: 'Повтор (поставщик точно не принял)',
  delivered: 'Доставлено',
  read: 'Прочитано',
  clicked: 'Нажато',
  failed: 'Отказ',
  unknown: 'Неизвестный исход',
  superseded: 'Заменён другим маршрутом',
  cancelled: 'Отменено',
  refunded: 'Деньги возвращены',
  cost: 'Цена от поставщика',
  price: 'Цена от поставщика',
  deadline: 'Дедлайн шага',
  advanced: 'Переход к следующему шагу',
  signal: 'Сигнал от вашего сервера',
  goal: 'Цель достигнута',
  code_check_failed: 'Введён неверный код',
  code_verified: 'Код подтверждён',
  succeeded: 'Успех политики',
  finished: 'Уведомление завершено',
  reconciled: 'Сверено сотрудником MailOcean',
  'email.queued': 'Письмо в очереди',
  'email.handed_to_provider': 'Письмо передано провайдеру',
  'email.provider_accepted': 'Письмо принято провайдером',
  'email.delivered': 'Письмо доставлено',
  'email.bounced': 'Письмо отскочило',
  'email.delivery_unknown': 'Исход письма неизвестен',
  'email.cancelled': 'Письмо отменено',
  'email.failed': 'Письмо не отправлено',
};

export function eventTone(type: string): string {
  if (['delivered', 'read', 'clicked', 'code_verified', 'succeeded', 'goal', 'email.delivered'].includes(type)) return 'good';
  if (['failed', 'email.bounced', 'email.failed', 'code_check_failed'].includes(type)) return 'bad';
  if (['unknown', 'waiting', 'held', 'retry_scheduled', 'skipped', 'email.delivery_unknown', 'deadline'].includes(type)) return 'warn';
  return '';
}

// ---------- API types ----------

export interface Money {
  amount: string | null;
  currency: 'EUR';
}

export interface ChannelSummary {
  channel: Channel;
  label: string;
  connected: boolean;
  accounts: number;
  verified_live: boolean;
  disabled: boolean;
  paused: boolean;
  paused_text?: string;
  identities: number | null;
  last_7_days: { sent: number; accepted: number; delivered: number; read: number; failed: number; unknown: number; skipped: number; cost: Money };
}

export interface ChannelAccount {
  id: string;
  channel: NonEmailChannel;
  provider: string;
  name: string;
  config: Record<string, unknown>;
  status: 'pending' | 'active' | 'failing' | 'disabled';
  verified_live: boolean;
  has_secret: boolean;
  platform: boolean;
  last_check_at: string | null;
  last_check_error: string | null;
  created_at: string;
  connect?: { webhook_url?: string; verify_token?: string; mode?: string; note?: string; templates_synced?: number };
  check_error?: string;
}

export interface PolicyStep {
  channel: Channel;
  deadline_seconds?: number;
  success?: Signal;
  account?: string;
}
export interface PolicyDef {
  kind: 'verify' | 'message';
  success?: 'code_verified' | Signal | 'goal';
  goal?: { event: string };
  code?: { length?: number; ttl_seconds?: number; max_attempts?: number };
  steps: PolicyStep[];
  parallel?: { channel: Channel; account?: string }[];
  countries?: { allow: string[] };
  max_cost?: { amount: string; currency: 'EUR' };
  quiet_hours?: 'ignore' | 'respect' | 'skip';
  on_unknown?: 'stop' | 'continue';
  on_timeout?: 'stop' | 'continue';
}
export interface PolicyRow {
  id?: string;
  key: string;
  version: number;
  hash?: string;
  definition: PolicyDef;
  source?: string;
  builtin: boolean;
  current_version?: number;
  archived?: boolean;
  created_by?: string | null;
  created_at?: string;
}
export interface PolicyCheck {
  valid: true;
  definition: PolicyDef;
  hash: string;
  country: string;
  steps: { channel: Channel; label: string; signal: Signal; configured: boolean; estimated_cost: string | null; paid_by: string; warning?: string; deadline_seconds?: number }[];
  parallel: { channel: Channel; label: string; signal: Signal; configured: boolean; estimated_cost: string | null; paid_by: string; warning?: string }[];
  estimated_cost_max: Money;
}

export interface Variants {
  email?: { from: string; from_name?: string; reply_to?: string; template?: { alias: string; version?: number }; subject?: string; text?: string; html?: string };
  sms?: { text: string };
  whatsapp?: { name: string; language: string; category: 'utility' | 'authentication' | 'marketing'; body_params?: string[]; button_params?: string[] };
  whatsapp_session?: { text: string };
  telegram?: { html: string; button?: { text: string; url: string } | null };
  push?: { title: string; body: string; url?: string | null; image?: string | null };
  web_push?: { title: string; body: string; url?: string | null; icon?: string | null; require_interaction?: boolean };
}
export interface ChannelTemplate {
  id: string;
  key: string;
  name: string;
  version: number;
  variants: Variants;
  variables: string[];
  channels: Channel[];
  archived: boolean;
  created_at: string;
  updated_at: string;
  versions?: { version: number; variables: string[]; created_by: string; created_at: string }[];
}

export interface NotificationStep {
  seq: number;
  channel: Channel;
  parallel?: boolean;
  state: string;
  skip_reason?: string;
  fail_code?: string;
  estimated_cost: string | null;
  cost?: string | null;
  paid_by: 'platform' | 'tenant' | 'free';
  country?: string | null;
  segments?: number;
  provider?: string;
  email_id?: string;
  deadline_seconds?: number;
  deadline_at?: string;
  /** A staff member reconciled the step's unknown outcome with the provider. */
  reconciled_at?: string;
  times_ms: { started: number | null; handed: number | null; accepted: number | null; delivered: number | null; read: number | null; finished: number | null };
}
export interface TimelineItem {
  at: string;
  elapsed_ms: number;
  type: string;
  seq: number | null;
  source: string;
  data: Record<string, unknown>;
}
export interface Notification {
  id: string;
  status: NotificationStatus;
  stop_reason?: string;
  kind: 'message' | 'verify';
  stream: 'transactional' | 'marketing';
  policy: { key: string; version: number };
  template?: { id: string; version: number };
  verification_id?: string;
  contact_id: string | null;
  current_seq: number;
  succeeded_seq: number | null;
  steps: NotificationStep[];
  estimated_cost_max: Money;
  cost: Money & { final: boolean };
  max_cost?: Money;
  metadata: Record<string, unknown>;
  tags: Record<string, string>;
  created_at: string;
  finished_at: string | null;
  timeline?: TimelineItem[];
  warnings?: { code: string; message: string; path?: string }[];
}

export interface ChannelSettingsView {
  allowed_countries: string[];
  supported_countries: string[];
  disabled_channels: NonEmailChannel[];
  quiet_hours: { start: string; end: string } | null;
  default_quiet_hours: { start: string; end: string };
  attestations: Record<string, { by: string; at: string; text_version: number } | null>;
  attestation_texts: Record<string, { text: string; version: number }>;
  daily_spend_limit: Money & { own: boolean; editable_by: string };
  spent_today: Money;
  staff_paused: Record<string, { public_text: string | null }>;
  paused_countries: string[];
}

export interface Identity {
  id: string;
  kind: 'phone' | 'telegram_chat' | 'push_fcm' | 'push_apns' | 'web_push';
  hint: string;
  country: string | null;
  status: 'active' | 'invalid' | 'revoked';
  unavailable: Record<string, { reason: string; at: string }>;
  verified_at: string | null;
  verified_by: string | null;
  account: string | null;
  contact_id: string | null;
  last_used_at: string | null;
  created_at: string;
  consents?: Record<string, { transactional: string; marketing: string; suppressed?: boolean }>;
}

export interface StepLogItem {
  id: string;
  notification_id: string;
  seq: number;
  parallel: boolean;
  channel: Channel;
  state: string;
  skip_reason: string | null;
  fail_code: string | null;
  provider: string | null;
  country: string | null;
  segments: number | null;
  paid_by: string;
  cost: string | null;
  cost_final: boolean;
  address: string | null;
  email_id: string | null;
  kind: 'message' | 'verify';
  stream: string;
  notification_status: NotificationStatus;
  handed_at: string | null;
  accepted_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  created_at: string;
}

export const IDENTITY_KIND: Record<Identity['kind'], string> = {
  phone: 'Телефон',
  telegram_chat: 'Чат Telegram',
  push_fcm: 'Устройство Android/FCM',
  push_apns: 'Устройство iOS/APNs',
  web_push: 'Подписка Web Push',
};
export const IDENTITY_CHANNELS: Record<Identity['kind'], NonEmailChannel[]> = {
  phone: ['sms', 'whatsapp', 'telegram_gateway'],
  telegram_chat: ['telegram_bot'],
  push_fcm: ['push'],
  push_apns: ['push'],
  web_push: ['web_push'],
};
export const CONSENT_LABEL: Record<string, { label: string; tone: string }> = {
  granted: { label: 'согласие', tone: 'ok' },
  withdrawn: { label: 'отзыв', tone: 'bad' },
  unknown: { label: 'нет записи', tone: 'cancelled' },
};
export const SUPPRESSION_REASON: Record<string, string> = { stop_keyword: 'ответил STOP', blocked: 'заблокировал', invalid: 'адрес недействителен', complaint: 'жалоба', manual: 'вручную' };

export const COUNTRY_NAME: Record<string, string> = {
  AT: 'Австрия', BE: 'Бельгия', BG: 'Болгария', HR: 'Хорватия', CY: 'Кипр', CZ: 'Чехия', DK: 'Дания', EE: 'Эстония', FI: 'Финляндия', FR: 'Франция',
  DE: 'Германия', GR: 'Греция', HU: 'Венгрия', IE: 'Ирландия', IT: 'Италия', LV: 'Латвия', LT: 'Литва', LU: 'Люксембург', MT: 'Мальта', NL: 'Нидерланды',
  PL: 'Польша', PT: 'Португалия', RO: 'Румыния', SK: 'Словакия', SI: 'Словения', ES: 'Испания', SE: 'Швеция', IS: 'Исландия', LI: 'Лихтенштейн', NO: 'Норвегия',
};
export const countryName = (c: string | null | undefined) => (c ? t(COUNTRY_NAME[c] ?? c) : '—');

// ---------- money and time ----------

/** "0.0840" → "€0,084"; null → «цены нет». */
export function eur(amount: string | number | null | undefined, digits = 4): string {
  if (amount == null || amount === '') return t('цены нет');
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '€0';
  const s = n.toFixed(digits).replace(/0+$/, '').replace(/\.$/, '');
  return `€${s.replace('.', ',')}`;
}

export function seconds(s: number | undefined): string {
  if (s == null) return '—';
  if (s < 60) return t('{n} с', { n: s });
  if (s < 3600) return t('{n} мин', { n: (Math.round(s / 6) / 10).toLocaleString(currentLocale()) });
  return t('{n} ч', { n: (Math.round(s / 360) / 10).toLocaleString(currentLocale()) });
}

export function msText(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n < 1000) return t('{n} мс', { n });
  if (n < 60_000) return t('{n} с', { n: (n / 1000).toLocaleString(currentLocale(), { minimumFractionDigits: 3, maximumFractionDigits: 3 }) });
  const m = Math.floor(n / 60_000);
  return t('{m} мин {s} с', { m, s: Math.round((n % 60_000) / 1000) });
}

// ---------- SMS segments (the same rules as src/channels/sms-segments.ts) ----------

const GSM_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXTENSION = '\f^{}\\[~]|€';
const BASIC = new Set(GSM_BASIC);
const EXTENSION = new Set(GSM_EXTENSION);

export interface SmsCount {
  encoding: 'gsm7' | 'ucs2';
  units: number;
  segments: number;
  perSegment: number;
  remaining: number;
  /** First characters that force UCS-2 (shown to the writer). */
  unicode: string[];
}

export function smsSegments(text: string): SmsCount {
  const unicode: string[] = [];
  for (const ch of text) if (!BASIC.has(ch) && !EXTENSION.has(ch) && !unicode.includes(ch) && unicode.length < 5) unicode.push(ch);
  if (!unicode.length) {
    let units = 0;
    for (const ch of text) units += EXTENSION.has(ch) ? 2 : 1;
    const single = units <= 160;
    const per = single ? 160 : 153;
    const segments = units === 0 ? 1 : single ? 1 : Math.ceil(units / 153);
    return { encoding: 'gsm7', units, segments, perSegment: per, remaining: segments * per - units, unicode };
  }
  const units = text.length;
  const single = units <= 70;
  const per = single ? 70 : 67;
  const segments = units === 0 ? 1 : single ? 1 : Math.ceil(units / 67);
  return { encoding: 'ucs2', units, segments, perSegment: per, remaining: segments * per - units, unicode };
}

/** Variables {{x}} / {{{x}}} of a text, in order of appearance. */
export function variablesOf(text: string): string[] {
  const out: string[] = [];
  const re = /\{\{\{?\s*([A-Za-z_][\w.]*)\s*\}?\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) if (!out.includes(m[1])) out.push(m[1]);
  return out;
}

/** Which variant key a channel uses in a template. */
export function variantOf(c: Channel): keyof Variants | null {
  switch (c) {
    case 'telegram_bot':
      return 'telegram';
    case 'telegram_gateway':
      return null;
    default:
      return c as keyof Variants;
  }
}

const py = (json: string) => json.replace(/\btrue\b/g, 'True').replace(/\bfalse\b/g, 'False').replace(/\bnull\b/g, 'None');

/** A policy as code: the same definition through the API (cURL / Node / Python / Go); the same hash is the same version. */
export function policySnippets(key: string, def: PolicyDef): Record<string, string> {
  const host = window.location.origin;
  const json = JSON.stringify(def, null, 2);
  return {
    cURL: `curl -X PUT ${host}/v1/cascade-policies/${key} \
  -H "Authorization: Bearer $MAILOCEAN_KEY" \
  -H "Content-Type: application/json" \
  -d '${json.replace(/'/g, "'\''")}'`,
    'Node.js': `import { MailOcean } from '@mailocean/sdk';

const mo = new MailOcean(process.env.MAILOCEAN_KEY!);
${t('// Та же политика = та же версия: повтор безопасен')}
await mo.cascadePolicies.put('${key}', ${json});`,
    Python: `import os
from mailocean import MailOcean

mo = MailOcean(os.environ["MAILOCEAN_KEY"])
mo.cascade_policies.put("${key}", ${py(json)})`,
    Go: `var def mailocean.PolicyDefinition
_ = json.Unmarshal([]byte(\`${json}\`), &def)
_, err := client.CascadePolicies.Put(ctx, "${key}", def)`,
  };
}

/** Sending a notification with a policy and a template (the «Как код» block of a template). */
export function sendSnippets(policy: string, template: string, data: Record<string, unknown>): Record<string, string> {
  const host = window.location.origin;
  const json = JSON.stringify({ to: { contact_id: 'con_…' }, policy, template, data }, null, 2);
  const dataJson = JSON.stringify(data);
  return {
    cURL: `curl -X POST ${host}/v1/notifications \
  -H "Authorization: Bearer $MAILOCEAN_KEY" \
  -H "Idempotency-Key: order-1042-shipped" \
  -H "Content-Type: application/json" \
  -d '${json.replace(/'/g, "'\''")}'`,
    'Node.js': `${t('// SDK ставит Idempotency-Key сам и повторяет запрос с тем же ключом')}
const n = await mo.notifications.send(${json});`,
    Python: `n = mo.notifications.send(
    to={"contact_id": "con_…"},
    policy="${policy}",
    template="${template}",
    data=${py(dataJson)},
)`,
    Go: `n, err := client.Notifications.Send(ctx, mailocean.NotificationParams{
	To:       mailocean.NotificationTo{ContactID: "con_…"},
	Policy:   "${policy}",
	Template: "${template}",
	Data:     map[string]any{ /* ${Object.keys(data).join(', ')} */ },
})`,
  };
}
